import { useMemo } from 'react';

import { DualAxes } from '@ant-design/charts';
import { Card } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatLongDate } from 'scenes/RouteReport/utils/time';

import { useChartClick } from '../hooks/useChartClick';
import {
  DayPoint,
  LINE_COLOR,
  VISIT_COLORS,
  VISIT_LABEL_COLORS,
  dayAxisMax,
  dayTickLabel,
} from '../utils/charts';
import { formatDecimal } from '../utils/format';
import { WHITE } from '../utils/palette';
import { SelectionScope, isDimmed } from '../utils/selectionScope';

const I18N_BASE = 'scenes.visit_history.days_chart';
const DIMMED_OPACITY = 0.45;
/** Above this many days the labels drop the weekday to fit. */
const DENSE_FROM_DAYS = 15;

interface VisitsByDayChartProps {
  points: DayPoint[];
  scope: SelectionScope;
  onSelectDay: (date: string) => void;
}

/**
 * One stacked column per day of the range (executed / incidents / pending)
 * and the compliance line on a secondary 0-100 axis. Days without routes stay
 * as an empty column so the range reads without holes.
 */
function VisitsByDayChart({
  points,
  scope,
  onSelectDay,
}: VisitsByDayChartProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const onEvent = useChartClick((datum) => {
    // A day without routes has nothing to select.
    if (points.find((p) => p.date === datum.date)?.empty === false) {
      onSelectDay(datum.date);
    }
  });

  const series = useMemo(
    () => ({
      executed: t(`${I18N_BASE}.series.executed`),
      incidents: t(`${I18N_BASE}.series.incidents`),
      pending: t(`${I18N_BASE}.series.pending`),
      compliance: t(`${I18N_BASE}.series.compliance`),
    }),
    [t]
  );

  const config = useMemo(() => {
    const dates = points.map((p) => p.date);
    const dense = points.length >= DENSE_FROM_DAYS;
    const byDate = new Map(points.map((p) => [p.date, p]));
    const yMax = dayAxisMax(points);
    const pct = (p: DayPoint) =>
      p.compliancePct === null
        ? '-'
        : `${formatDecimal(p.compliancePct, language)}%`;

    const barRows = points.flatMap((p) => [
      { date: p.date, category: series.executed, value: p.executed },
      { date: p.date, category: series.incidents, value: p.incidents },
      { date: p.date, category: series.pending, value: p.pending },
    ]);
    const rateRows = points.map((p) => ({
      date: p.date,
      rate: p.compliancePct,
    }));
    // Each category maps explicitly to its own label color.
    const labelFill: Record<string, string> = {
      [series.executed]: VISIT_LABEL_COLORS.executed,
      [series.incidents]: VISIT_LABEL_COLORS.incident,
      [series.pending]: VISIT_LABEL_COLORS.pending,
    };
    const xScale = { domain: dates };
    const rightScale = {
      independent: true,
      domain: [0, 100],
      nice: false,
    };
    const item =
      (
        name: string,
        color: string | undefined,
        value: (p: DayPoint) => string | number
      ) =>
      (datum: any) => {
        const p = byDate.get(datum.date) as DayPoint;

        return { name, value: value(p), color };
      };

    return {
      xField: 'date',
      legend: false,
      onEvent,
      children: [
        {
          type: 'interval' as const,
          data: barRows,
          yField: 'value',
          colorField: 'category',
          stack: true,
          style: {
            maxWidth: 44,
            fillOpacity: (d: any) =>
              isDimmed(scope, 'date', d.date) ? DIMMED_OPACITY : 1,
          },
          scale: {
            x: xScale,
            y: { domain: [0, yMax], nice: false },
            color: {
              domain: [series.executed, series.incidents, series.pending],
              range: [
                VISIT_COLORS.executed,
                VISIT_COLORS.incident,
                VISIT_COLORS.pending,
              ],
            },
          },
          // Counts inside each segment; zeros print nothing and a label that
          // does not fit its segment is hidden.
          labels: [
            {
              text: (d: any): string => (d.value > 0 ? String(d.value) : ''),
              position: 'inside',
              fontSize: 10,
              fontWeight: 600,
              // White on Navy / Steel Gray, Slate Dark on Amber.
              fill: (d: any): string => labelFill[d.category as string],
              transform: [{ type: 'overflowHide' }],
            },
          ],
          axis: {
            x: {
              labelAutoRotate: false,
              labelFormatter: (date: string) => {
                const p = byDate.get(date);
                const day = dayTickLabel(date, language, dense);
                const under =
                  !p || p.empty
                    ? t(`${I18N_BASE}.no_routes`)
                    : `${p.executed} / ${p.planned}`;

                return `${day}\n${under}`;
              },
            },
            y: { title: t(`${I18N_BASE}.axis.visits`), position: 'left' },
          },
          tooltip: {
            title: (d: any) => formatLongDate(d.date),
            items: [
              item(
                t(`${I18N_BASE}.tooltip.planned`),
                undefined,
                (p) => p.planned
              ),
              item(series.executed, VISIT_COLORS.executed, (p) => p.executed),
              item(series.incidents, VISIT_COLORS.incident, (p) => p.incidents),
              item(series.pending, VISIT_COLORS.pending, (p) => p.pending),
              item(series.compliance, LINE_COLOR, pct),
              item(
                t(`${I18N_BASE}.tooltip.routes`),
                undefined,
                (p) => p.routes
              ),
            ],
          },
        },
        {
          type: 'line' as const,
          data: rateRows,
          yField: 'rate',
          // Days without routes carry `null`: the line breaks there.
          style: { stroke: LINE_COLOR, lineWidth: 2 },
          scale: { x: xScale, y: rightScale },
          axis: {
            y: {
              position: 'right',
              title: false,
              grid: false,
              labelFormatter: (v: number) => `${v}%`,
            },
          },
          tooltip: false,
        },
        {
          type: 'point' as const,
          data: rateRows.filter((row) => row.rate !== null),
          yField: 'rate',
          // Hollow Teal marker, like the line: the ring reads on the white
          // plot and the white center over the bars. The value shows only on
          // hover.
          style: {
            r: 5,
            lineWidth: 2.5,
            stroke: LINE_COLOR,
            fill: WHITE,
            // The ring carries the color now, so it dims with the day.
            strokeOpacity: (d: any) =>
              isDimmed(scope, 'date', d.date) ? DIMMED_OPACITY : 1,
          },
          scale: { x: xScale, y: rightScale },
          axis: { y: false },
          tooltip: {
            title: (d: any) => formatLongDate(d.date),
            items: [item(series.compliance, LINE_COLOR, pct)],
          },
        },
      ],
    };
  }, [points, scope, series, language, t, onEvent]);

  return (
    <Card
      title={
        <>
          {t(`${I18N_BASE}.title`)}
          <span className="visit-history-chart-head__hint">
            {t(`${I18N_BASE}.click_hint`)}
          </span>
        </>
      }
      styles={{ body: { paddingTop: 12 } }}
    >
      <div className="visit-history-chart-head">
        <span className="visit-history-chart-head__subtitle">
          {t(`${I18N_BASE}.subtitle`)}
        </span>
        <div className="visit-history-legend">
          <span>
            <i style={{ background: VISIT_COLORS.executed }} />
            {series.executed}
          </span>
          <span>
            <i style={{ background: VISIT_COLORS.incident }} />
            {series.incidents}
          </span>
          <span>
            <i style={{ background: VISIT_COLORS.pending }} />
            {series.pending}
          </span>
          <span>
            <i
              className="visit-history-legend__line"
              style={{ background: LINE_COLOR }}
            />
            {series.compliance}
          </span>
        </div>
      </div>
      <DualAxes {...(config as any)} height={320} />
    </Card>
  );
}

export default VisitsByDayChart;
